import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import * as THREE from "three";
import { useMapStore } from "../../store/useMapStore.js";
import { useSiteExportStore } from "../../store/useSiteExport.js";
import { useEntityStore } from "../../store/useExportStore.js";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { geometryToJSON, CreateShapegeometry, CreateShapegeometryWithMultipleVoids} from "../../utils/GeometryHelper.js";
import GUI from "lil-gui";
import { Stage, GeometryType } from "../../constants/progress.js";
import { useProgressStore } from "../../store/progressStore.js";
import { useGeometrySelectionStore } from "../../store/useGeometrySelectionStore.js";
import isGeometryValid from "../../services/GeometryValidation.js";

const PolygonPlane = ({ 
  entities, 
  selectedEntityId, 
  setSelectedEntityId,
  inputColor,
  inputVisibilty,
  onEntitySelect,
  className,
  crs,
  geometryType,
  zTransform,
  zTransformEdges,
  }) => {
  const updateProgress = useProgressStore.getState().updateProgress;
  const meshRef = useRef();
  const clearSite = useEntityStore((state) => state.clearSite);
  const setSiteEntity = useEntityStore((state) => state.setSiteEntity);
  const { selectParcel, isParcelSelected, selectedType } = useGeometrySelectionStore();
 
  
  const { mergedGeometry, mergedEdgesGeometry, entityIndices, vertexToEntity, sites } = useMemo(() => {
    const geometries = [];
    const edgeGeometries = [];
    const indices = [];
    const vertexIndices = [];
    const sites = [];
    updateProgress(geometryType,{
      stage: Stage.CREATE,
      message:'Create geometries',
      progress: 75,
      error: null
     
    });

    entities.forEach((entity, entityIndex) => {
      const siteExport = {
        siteProperties: {
          amtlicheFlaeche: entity.amtlicheFlaeche,
          gemarkungName: entity.gemarkungName,
          flurnummer: entity.flurnumme,
          flurstücknummer: entity.flurstücknummer,
        },
        surfaces: {
          ground: [],
        }
      };
      
      let coords;
      if (crs == "EPSG3857" || crs == "EPSG4326"|| crs == "EPSG25832" || crs == "EPSG25833") {
        coords = entity.mercatorCoordinates; 
      }
      const geometry = CreateSurface(coords, entity);
      if(!geometry)
      {console.log("Failed Creating Parcel geometry ",entity)}
     
      if (geometry) {
        const triangleCount = geometry.index ? 
        geometry.index.count / 3 : 
        geometry.attributes.position.count / 3;
        for (let i = 0; i < triangleCount; i++) {
          indices.push(entityIndex);
        }
        
        // Map each vertex to entity
        const vertexCount = geometry.attributes.position.count;
        for (let i = 0; i < vertexCount; i++) {
          vertexIndices.push(entityIndex);
        }
        const jsonGeom = geometryToJSON(geometry);
        siteExport.surfaces.ground.push(jsonGeom);
        geometries.push(geometry);
        edgeGeometries.push(new THREE.EdgesGeometry(geometry));
      }
      if(!geometry)
      {

        //console.log("Parcels geometry not created ", entity)
      }
      sites.push(siteExport);
    });
    
    let mergedGeometry, mergedEdgesGeometry;
    if (geometries.length > 0) {
      updateProgress(geometryType,{
      stage: Stage.CREATED,
      message:'Create geometries completed',
      created: geometries.length,
      progress: 100,
      error: null
     
    });
    if(className =="FlurstuckEntity")
    { console.log("Parcel geometries sucessfuly created ",geometries.length);}
     
      mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries, false);
      mergedEdgesGeometry = BufferGeometryUtils.mergeGeometries(edgeGeometries, false);
      
      // Initialize vertex colors with base color
      const positionCount = mergedGeometry.attributes.position.count;
      const colors = new Float32Array(positionCount * 3);
      const baseColor = inputColor;
      
      for (let i = 0; i < positionCount; i++) {
        colors[i * 3] = baseColor.r;
        colors[i * 3 + 1] = baseColor.g;
        colors[i * 3 + 2] = baseColor.b;
      }
      
      mergedGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      updateProgress(geometryType,{
      stage: Stage.DONE,
      message:'Create geometries completed',
      created: geometries.length,
      progress: 100,
      error: null
     
    });
    } else {
      updateProgress(geometryType,{
      stage: Stage.ERROR,
      message:'Failed to create geometries',
      error:"Error creating geometries"
     
    });
      console.log("No geometries found/creating geometries failed!");
    }
    
    return { mergedGeometry, mergedEdgesGeometry, entityIndices: indices, vertexToEntity: vertexIndices, sites };
  }, [inputVisibilty, entities, crs]); 

  // Reset colors when selection is cleared (background or other geometry clicked)
  useEffect(() => {
    if (!mergedGeometry || !mergedGeometry.attributes.color) return;
    
    // If no parcel is selected (background clicked or other geometry selected)
    if (selectedType !== 'parcel') {
      const positionCount = mergedGeometry.attributes.position.count;
      const colors = mergedGeometry.attributes.color.array;
      const baseColor = new THREE.Color(inputColor);
      
      // Reset all vertices to base color
      for (let i = 0; i < positionCount; i++) {
        colors[i * 3] = baseColor.r;
        colors[i * 3 + 1] = baseColor.g;
        colors[i * 3 + 2] = baseColor.b;
      }
      
      mergedGeometry.attributes.color.needsUpdate = true;
      setSelectedEntityId(null);
    }
    // If a different parcel is selected, check if our current selection needs to be cleared
    else if (selectedEntityId && !isParcelSelected(selectedEntityId)) {
      const positionCount = mergedGeometry.attributes.position.count;
      const colors = mergedGeometry.attributes.color.array;
      const baseColor = new THREE.Color(inputColor);
      
      // Reset all vertices to base color
      for (let i = 0; i < positionCount; i++) {
        colors[i * 3] = baseColor.r;
        colors[i * 3 + 1] = baseColor.g;
        colors[i * 3 + 2] = baseColor.b;
      }
      
      mergedGeometry.attributes.color.needsUpdate = true;
      setSelectedEntityId(null);
    }
  }, [selectedType, selectedEntityId, isParcelSelected, mergedGeometry, inputColor, setSelectedEntityId]);

  useEffect(() => {
    if (inputVisibilty) {
      setSiteEntity({ type: "sites", sites });
    }
    
    if (!inputVisibilty) {
      clearSite();
    }
  }, [inputVisibilty, sites, setSiteEntity, clearSite]);

  

 const handleClick = useCallback((event) => {
    if (!meshRef.current || !inputVisibilty) return;

    const intersects = event.intersections;
    if (!intersects || intersects.length === 0) return;

    // Only consider intersections with THIS mesh (parcel layer), ignore buildings etc.
    const ownIntersections = intersects.filter(
      (i) => i.object === meshRef.current
    );

    if (ownIntersections.length === 0) return;

    // Take the closest hit only
    const closest = ownIntersections[0];
    const faceIndex = closest.faceIndex;

    if (faceIndex === undefined || entityIndices[faceIndex] === undefined) return;

    const selectedEntityIndex = entityIndices[faceIndex];
    const selectedEntity = entities[selectedEntityIndex];

    if (!selectedEntity) return;
    console.log("Selected Entity: ", selectedEntity);
    event.stopPropagation();
    selectParcel(selectedEntity.id);
    setSelectedEntityId(selectedEntity.id);

    if (onEntitySelect) {
      onEntitySelect(selectedEntity);
    }

    if (mergedGeometry && mergedGeometry.attributes.color) {
      const positionCount = mergedGeometry.attributes.position.count;
      const colors = mergedGeometry.attributes.color.array;
      const baseColor = new THREE.Color(inputColor);
      const selectedColor = new THREE.Color('#2B323A');

      for (let i = 0; i < positionCount; i++) {
        const vertexEntityIndex = vertexToEntity[i];
        const color = vertexEntityIndex === selectedEntityIndex ? selectedColor : baseColor;
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      }

      mergedGeometry.attributes.color.needsUpdate = true;
    }
  }, [entities, entityIndices, vertexToEntity, onEntitySelect, inputVisibilty, mergedGeometry, inputColor, setSelectedEntityId, selectParcel]);
  if (!mergedGeometry) return null;

  return (
    <group visible={inputVisibilty} position={zTransform} >
      {inputVisibilty && (
        <mesh
          ref={meshRef} 
          geometry={mergedGeometry} 
          onClick={handleClick}
          polygonOffset
          polygonOffsetFactor={1.5}
          polygonOffsetUnits={1.5}
        >
          <meshStandardMaterial 
            vertexColors={true}
            side={THREE.DoubleSide}
            transparent={false}
          />
        </mesh>
      )}
      
      {/* Black outline */}
      {mergedEdgesGeometry && (
        <lineSegments geometry={mergedEdgesGeometry} position={[0,0.5,0]}>
          <lineBasicMaterial color="black" />
        </lineSegments>
      )}
    </group>
  );
};



export default PolygonPlane;

// Helper functions below...


function isFlatRing(coords) {
  // A flat ring's first element is a number-pair [x, y], not another array-of-arrays
  return (
    Array.isArray(coords) &&
    coords.length > 0 &&
    Array.isArray(coords[0]) &&
    typeof coords[0][0] === "number"
  );
}

function normalizeToFlatRing(coords) {
  if (!Array.isArray(coords)) return [];
  if (isFlatRing(coords)) return coords; // Already flat — done
  return normalizeToFlatRing(coords[0]); // Unwrap one level and try again
}


function normalizeCoordinates(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return [];

  
  if (isFlatRing(coordinates)) {
    return [coordinates];
  }

  
  return coordinates.map(normalizeToFlatRing);
}

// ─── Main Function ────────────────────────────────────────────────────────────

  function CreateSurface(coordinates, entity) {
    if (!coordinates || coordinates.length === 0) {
      //console.warn("Empty coordinates provided");
      return null;
    }
    const rings = normalizeCoordinates(coordinates);

    if (rings.length === 0) {
      console.warn("No valid rings after normalization");
      return null;
    }
    const mainRingRaw = rings[0];
    const holesRaw = rings.slice(1); // Empty array if no holes — that's fine
    const mainRing = mainRingRaw.slice(0, -1);
    const holes = holesRaw.map((hole) => hole.slice(0, -1));
    if (mainRing.length < 3) {
      console.warn("Main ring too small:", mainRing.length, entity);
      return null;
    }
    const  [validHoles,invalidHoles] = validateHoles(mainRing, holes);
    if (validHoles.length > 0)
    {
      const polygonwHole = CreateShapegeometryWithMultipleVoids(mainRing, validHoles);
      return polygonwHole;
    } 
    if(invalidHoles.length > 0 )
    {
      const mainRingGeometry = CreateShapegeometry(mainRing);
      const invalidGeometries = invalidHoles
        .map(hole => CreateShapegeometry(hole))
        .filter(Boolean); // remove any nulls
      const merged = BufferGeometryUtils.mergeGeometries([mainRingGeometry, ...invalidGeometries]);
      return merged;
    }
    else
    {
      return CreateShapegeometry(mainRing);
    }
  }

function isPointInPolygon(point, polygon) {
    const x = point[0], y = point[1];
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];

      const intersects =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersects) inside = !inside;
    }

    return inside;
  }
function getRingCentroid(ring) {
    const x = ring.reduce((sum, pt) => sum + pt[0], 0) / ring.length;
    const y = ring.reduce((sum, pt) => sum + pt[1], 0) / ring.length;
    return [x, y];
  }
function isHoleInsideMainRing(hole, mainRing) {
    // Test first 3 points of the hole — if majority are inside, the hole is inside
    const testPoints = hole.slice(0, 3);
    const results = testPoints.map(pt => isPointInPolygon(pt, mainRing));
    const insideCount = results.filter(Boolean).length;

    return insideCount >= 2; // majority vote
  }

/**
 * Filters out holes that are not inside the main ring
 * Use this before calling CreateShapegeometryWithMultipleVoids
 */
function validateHoles(mainRing, holes) {
  if (!holes || holes.length === 0) return [[], []];


  const validHoles = [];
  const invalidHoles = [];

  holes.forEach((hole, index) => {
    if (isHoleInsideMainRing(hole, mainRing)) {
      validHoles.push(hole);
    } else {
      invalidHoles.push(hole);
    }
  });

  // if (invalidHoles.length > 0) {
  //   invalidHoles.forEach((hole, i) => {
  //   const   centroid = getRingCentroid(hole);
  //   //   console.log(`Invalid hole ${i} centroid:`, centroid);
  //   //   console.log(`  Is centroid inside mainRing?`, isPointInPolygon(centroid, mainRing));
  //   //   console.log(`  MainRing X range:`, 
  //   //     Math.min(...mainRing.map(p => p[0])), "→", Math.max(...mainRing.map(p => p[0])));
  //   //   console.log(`  MainRing Y range:`, 
  //   //     Math.min(...mainRing.map(p => p[1])), "→", Math.max(...mainRing.map(p => p[1])));
  //   //   console.log(`  Hole X range:`, 
  //   //     Math.min(...hole.map(p => p[0])), "→", Math.max(...hole.map(p => p[0])));
  //   //   console.log(`  Hole Y range:`, 
  //   //     Math.min(...hole.map(p => p[1])), "→", Math.max(...hole.map(p => p[1])));
  //   // });
  //   // console.warn(`Holes at indices [${invalidHoles.join(", ")}] are outside the main ring and will be skipped`);
  // }

  return [validHoles,invalidHoles];
}





